/**
 * Inspirations Grid Component
 * Handles filtering, pagination, and bundle popup functionality
 */

class InspirationsComponent extends HTMLElement {
  constructor() {
    super();

    this.grid = this.querySelector(".inspirations-grid");
    this.cards = Array.from(this.querySelectorAll(".inspiration-card"));
    this.filterTabs = this.querySelectorAll(".filter-tab");
    this.itemsPerPage = parseInt(this.grid.dataset.itemsPerPage) || 9;

    this.currentCategory = "all";
    this.currentPage = 1;

    this.initFilters();
    this.initPagination();
    this.initCardTriggers();
  }

  /**
   * Initialize filter tabs
   */
  initFilters() {
    this.filterTabs.forEach((tab) => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        const category = tab.dataset.category;
        this.filterByCategory(category);
      });
    });
  }

  /**
   * Filter inspirations by category
   */
  filterByCategory(category) {
    if (this.currentCategory === category) return;

    this.currentCategory = category;
    this.currentPage = 1;

    // Update active tab
    this.filterTabs.forEach((tab) => {
      tab.classList.toggle("active", tab.dataset.category === category);
    });

    // Add filtering class for animation
    this.grid.classList.add("is-filtering");

    // Filter cards with smooth transition
    this.cards.forEach((card, index) => {
      const cardCategories = card.dataset.categories
        ? card.dataset.categories.split(",")
        : [];
      const shouldShow =
        category === "all" || cardCategories.includes(category);

      setTimeout(() => {
        card.classList.toggle("hidden", !shouldShow);
      }, index * 30);
    });

    // Remove filtering class after animation
    setTimeout(
      () => {
        this.grid.classList.remove("is-filtering");
        this.updatePagination();
      },
      this.cards.length * 30 + 300,
    );
  }

  /**
   * Initialize pagination
   */
  initPagination() {
    this.paginationWrapper = this.querySelector(".inspirations-pagination");
    if (!this.paginationWrapper) return;

    this.prevBtn = this.paginationWrapper.querySelector(".pagination-prev");
    this.nextBtn = this.paginationWrapper.querySelector(".pagination-next");
    this.currentPageEl = this.paginationWrapper.querySelector(
      ".pagination-current",
    );
    this.totalPagesEl =
      this.paginationWrapper.querySelector(".pagination-total");

    this.prevBtn?.addEventListener("click", () =>
      this.goToPage(this.currentPage - 1),
    );
    this.nextBtn?.addEventListener("click", () =>
      this.goToPage(this.currentPage + 1),
    );

    this.updatePagination();
  }

  /**
   * Get visible cards
   */
  getVisibleCards() {
    return this.cards.filter((card) => !card.classList.contains("hidden"));
  }

  /**
   * Update pagination state
   */
  updatePagination() {
    if (!this.paginationWrapper) return;

    const visibleCards = this.getVisibleCards();
    const totalPages = Math.ceil(visibleCards.length / this.itemsPerPage);

    // Hide pagination if only one page
    if (totalPages <= 1) {
      this.paginationWrapper.style.display = "none";
      visibleCards.forEach((card) => (card.style.display = ""));
      return;
    }

    this.paginationWrapper.style.display = "flex";

    // Update pagination info
    if (this.currentPageEl) this.currentPageEl.textContent = this.currentPage;
    if (this.totalPagesEl) this.totalPagesEl.textContent = totalPages;

    // Update button states
    if (this.prevBtn) this.prevBtn.disabled = this.currentPage === 1;
    if (this.nextBtn) this.nextBtn.disabled = this.currentPage === totalPages;

    // Show/hide cards based on current page
    this.showCurrentPage(visibleCards);
  }

  /**
   * Show cards for current page
   */
  showCurrentPage(visibleCards) {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;

    this.cards.forEach((card) => {
      if (card.classList.contains("hidden")) {
        card.style.display = "none";
        return;
      }

      const cardIndex = visibleCards.indexOf(card);
      card.style.display =
        cardIndex >= startIndex && cardIndex < endIndex ? "" : "none";
    });

    // Scroll to top of grid smoothly
    this.grid.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /**
   * Go to specific page
   */
  goToPage(page) {
    const visibleCards = this.getVisibleCards();
    const totalPages = Math.ceil(visibleCards.length / this.itemsPerPage);

    if (page < 1 || page > totalPages) return;

    this.currentPage = page;
    this.updatePagination();
  }

  /**
   * Initialize card click triggers for popup
   */
  initCardTriggers() {
    this.cards.forEach((card) => {
      const trigger = card.querySelector("[data-inspiration-trigger]");
      const cardClick = card.querySelector("[data-card-click]");

      const openDrawer = (e) => {
        e.preventDefault();
        const inspirationData = JSON.parse(
          card.querySelector(".inspiration-data").textContent,
        );
        this.openBundleDrawer(inspirationData);
      };

      if (trigger) {
        trigger.addEventListener("click", openDrawer);
      }

      if (cardClick) {
        cardClick.addEventListener("click", openDrawer);
        cardClick.style.cursor = "pointer";
      }
    });
  }

  /**
   * Open bundle drawer with inspiration data
   */
  openBundleDrawer(data) {
    const drawer = document.querySelector("inspiration-bundle-drawer");
    if (drawer) {
      drawer.open(data);
    }
  }
}

/**
 * Inspiration Bundle Drawer Component
 * Handles the right-side popup for bundle products
 */
class InspirationBundleDrawer extends HTMLElement {
  constructor() {
    super();

    this.backdrop = this.querySelector(".inspiration-drawer__backdrop");
    this.closeBtn = this.querySelector(".inspiration-drawer__close");
    this.title = this.querySelector(".inspiration-drawer__title");
    this.image = this.querySelector(".inspiration-drawer__image");
    this.productsContainer = this.querySelector(
      ".inspiration-drawer__products",
    );
    this.totalPriceEl = this.querySelector(".total-price .money");
    this.addAllBtn = this.querySelector(".inspiration-drawer__add-all");
    this.errorWrapper = this.querySelector(
      ".inspiration-drawer__error-wrapper",
    );
    this.errorMessage = this.querySelector(
      ".inspiration-drawer__error-message",
    );

    this.currentData = null;
    this.productStates = {};

    this.initEventListeners();
  }

  /**
   * Initialize event listeners
   */
  initEventListeners() {
    // Close handlers
    this.closeBtn?.addEventListener("click", () => this.close());
    this.backdrop?.addEventListener("click", () => this.close());

    // Prevent closing when clicking inside panel
    this.querySelector(".inspiration-drawer__panel")?.addEventListener(
      "click",
      (e) => {
        e.stopPropagation();
      },
    );

    // Add all to cart handler
    this.addAllBtn?.addEventListener("click", () => this.addAllToCart());

    // Escape key to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.classList.contains("active")) {
        this.close();
      }
    });
  }

  /**
   * Open drawer with inspiration data
   */
  open(data) {
    this.currentData = data;
    this.productStates = {};

    // Set title and image
    if (this.title && typeof this.title === "object") {
      this.title.textContent = data.name;
    }
    if (this.image && data.image && typeof this.image === "object") {
      this.image.src = data.image;
      this.image.alt = data.name;
    }

    // Render variants
    this.renderVariants(data.variants);

    // Show drawer
    this.classList.add("active");
    document.body.style.overflow = "hidden";

    // Update total
    this.updateTotal();
  }

  /**
   * Close drawer
   */
  close() {
    this.classList.remove("active");
    document.body.style.overflow = "";

    // Clear after animation
    setTimeout(() => {
      this.productsContainer.innerHTML = "";
      this.productStates = {};
      this.hideError();
    }, 400);
  }

  /**
   * Render variants in drawer
   */
  renderVariants(variants) {
    if (!variants || variants.length === 0) {
      this.productsContainer.innerHTML =
        '<p class="inspirations-empty">Bu koleksiyonda ürün bulunmuyor.</p>';
      return;
    }

    this.productsContainer.innerHTML = variants
      .map((variant) => {
        this.productStates[variant.id] = {
          variantId: variant.id,
          quantity: 1,
          price: variant.price,
          compareAtPrice: variant.compare_at_price || null,
        };

        return this.createVariantHTML(variant);
      })
      .join("");

    // Attach event listeners
    this.attachProductListeners();
  }

  /**
   * Create HTML for a variant
   */
  createVariantHTML(variant) {
    const state = this.productStates[variant.id];
    const currentPrice = state.price;
    const currentComparePrice = state.compareAtPrice;
    const hasDiscount =
      currentComparePrice && currentComparePrice > currentPrice;
    const isAvailable = variant.available;

    const displayTitle =
      variant.product_title +
      (variant.title && variant.title !== "Default Title"
        ? " - " + variant.title
        : "");

    return `
      <div class="bundle-product-item" data-product-id="${variant.id}">
        <div class="bundle-product-item__image">
          ${variant.featured_image ? `<img src="${variant.featured_image}" alt="${displayTitle}" loading="lazy" />` : ""}
        </div>
        
        <div class="bundle-product-item__details">
          <h4 class="bundle-product-item__title">${displayTitle}</h4>
          
          <div class="bundle-product-item__price ${hasDiscount ? "bundle-product-item__price--sale" : ""}" data-price-display="${variant.id}">
            <span class="current-price">${this.formatMoney(currentPrice)}</span>
            ${hasDiscount ? `<span class="bundle-product-item__compare-price">${this.formatMoney(currentComparePrice)}</span>` : ""}
          </div>
          
          ${
            isAvailable
              ? `
            <div class="bundle-product-item__controls">
              <div class="bundle-product-item__quantity">
                <button class="quantity-button" data-quantity-decrease="${variant.id}" ${state.quantity <= 1 ? "disabled" : ""}>-</button>
                <input 
                  type="number" 
                  class="quantity-input" 
                  value="${state.quantity}" 
                  min="1" 
                  max="10"
                  data-quantity-input="${variant.id}"
                  readonly
                />
                <button class="quantity-button" data-quantity-increase="${variant.id}" ${state.quantity >= 10 ? "disabled" : ""}>+</button>
              </div>
            </div>
          `
              : `
            <div class="bundle-product-item__sold-out">Stokta Yok</div>
          `
          }
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners to variant elements
   */
  attachProductListeners() {
    // Quantity buttons
    this.productsContainer
      .querySelectorAll("[data-quantity-decrease]")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const productId = btn.dataset.quantityDecrease;
          this.updateQuantity(productId, -1);
        });
      });

    this.productsContainer
      .querySelectorAll("[data-quantity-increase]")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const productId = btn.dataset.quantityIncrease;
          this.updateQuantity(productId, 1);
        });
      });
  }

  /**
   * Update product quantity
   */
  updateQuantity(productId, change) {
    const state = this.productStates[productId];
    if (!state) return;

    const newQuantity = state.quantity + change;
    if (newQuantity < 1 || newQuantity > 10) return;

    state.quantity = newQuantity;

    // Update UI
    const input = this.productsContainer.querySelector(
      `[data-quantity-input="${productId}"]`,
    );
    if (input) input.value = newQuantity;

    const decreaseBtn = this.productsContainer.querySelector(
      `[data-quantity-decrease="${productId}"]`,
    );
    if (decreaseBtn) decreaseBtn.disabled = newQuantity <= 1;

    const increaseBtn = this.productsContainer.querySelector(
      `[data-quantity-increase="${productId}"]`,
    );
    if (increaseBtn) increaseBtn.disabled = newQuantity >= 10;

    this.updateTotal();
  }

  /**
   * Update price display for a product
   */
  updatePriceDisplay(productId) {
    const priceDisplay = this.productsContainer.querySelector(
      `[data-price-display="${productId}"]`,
    );
    if (!priceDisplay) return;

    const state = this.productStates[productId];
    const hasDiscount =
      state.compareAtPrice && state.compareAtPrice > state.price;

    priceDisplay.className = `bundle-product-item__price ${hasDiscount ? "bundle-product-item__price--sale" : ""}`;
    priceDisplay.innerHTML = `
      <span class="current-price">${this.formatMoney(state.price)}</span>
      ${hasDiscount ? `<span class="bundle-product-item__compare-price">${this.formatMoney(state.compareAtPrice)}</span>` : ""}
    `;
  }

  /**
   * Update total price
   */
  updateTotal() {
    const total = Object.values(this.productStates).reduce((sum, state) => {
      return sum + state.price * state.quantity;
    }, 0);

    if (this.totalPriceEl) {
      this.totalPriceEl.textContent = this.formatMoney(total);
    }
  }

  /**
   * Add all products to cart
   */
  async addAllToCart() {
    this.hideError();

    // Prepare items for cart
    const items = Object.entries(this.productStates).map(
      ([productId, state]) => ({
        id: state.variantId,
        quantity: state.quantity,
      }),
    );

    if (items.length === 0) {
      this.showError("Sepete eklenecek ürün bulunamadı.");
      return;
    }

    // Show loading state
    this.addAllBtn.classList.add("loading");

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          error.description || "Ürünler sepete eklenirken bir hata oluştu.",
        );
      }

      const result = await response.json();

      // Close this drawer first
      this.close();

      // Update cart and open cart drawer
      setTimeout(() => {
        // Fetch sections to update
        const sectionsToRender = [
          {
            id: "mini-cart",
            section: "mini-cart",
          },
          {
            id: "cart-icon-bubble",
            section: "cart-icon-bubble",
          },
          {
            id: "mobile-cart-icon-bubble",
            section: "mobile-cart-icon-bubble",
          },
        ];

        const sectionsUrl = sectionsToRender
          .map((section) => `sections=${section.section}`)
          .join("&");

        fetch(`${window.Shopify.routes.root}?${sectionsUrl}`)
          .then((response) => response.json())
          .then((sections) => {
            // Update each section
            sectionsToRender.forEach((section) => {
              const elementToReplace = document.getElementById(section.id);
              if (elementToReplace && sections[section.section]) {
                const html = new DOMParser()
                  .parseFromString(sections[section.section], "text/html")
                  .querySelector(".shopify-section");
                if (html) {
                  elementToReplace.innerHTML = html.innerHTML;
                }
              }
            });

            // Open cart drawer
            const miniCart = document.querySelector("mini-cart");
            const cartDrawer = document.querySelector("cart-drawer");

            if (miniCart && typeof miniCart.open === "function") {
              miniCart.open();
            } else if (
              cartDrawer &&
              typeof cartDrawer.openMenuDrawer === "function"
            ) {
              cartDrawer.openMenuDrawer();
            } else {
              // Fallback: click cart icon
              const cartIcon = document.querySelector(".header__icon--cart");
              if (cartIcon) {
                cartIcon.click();
              }
            }

            // Dispatch event
            document.dispatchEvent(new CustomEvent("cart:updated"));
          })
          .catch((error) => {
            console.error("Error updating cart:", error);
            // Still try to open cart drawer on error
            const cartIcon = document.querySelector(".header__icon--cart");
            if (cartIcon) {
              cartIcon.click();
            }
          });
      }, 300);
    } catch (error) {
      console.error("Error adding to cart:", error);
      this.showError(error.message);
    } finally {
      this.addAllBtn.classList.remove("loading");
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    if (this.errorWrapper && this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorWrapper.hidden = false;
    }
  }

  /**
   * Hide error message
   */
  hideError() {
    if (this.errorWrapper) {
      this.errorWrapper.hidden = true;
    }
  }

  /**
   * Show success feedback
   */
  showSuccess() {
    const originalText = this.addAllBtn.textContent;
    this.addAllBtn.textContent = "✓ Sepete Eklendi!";
    this.addAllBtn.style.background = "#4caf50";

    setTimeout(() => {
      this.addAllBtn.textContent = originalText;
      this.addAllBtn.style.background = "";
    }, 2000);
  }

  /**
   * Format money with Shopify's money format
   */
  formatMoney(cents) {
    if (typeof cents !== "number") {
      cents = parseInt(cents) || 0;
    }

    const money = (cents / 100).toFixed(2);

    // Try to get Shopify's money format from theme settings
    let moneyFormat = "{{amount}} TL"; // Default for Turkish Lira

    if (window.Shopify && window.Shopify.money_format) {
      moneyFormat = window.Shopify.money_format;
    } else if (window.theme && window.theme.moneyFormat) {
      moneyFormat = window.theme.moneyFormat;
    }

    // Format the money string
    let formattedMoney = money;

    // Turkish format: use comma as decimal separator
    if (moneyFormat.includes("TL") || moneyFormat.includes("₺")) {
      formattedMoney = money.replace(".", ",");
    }

    // Replace placeholders
    return moneyFormat
      .replace("{{amount}}", formattedMoney)
      .replace("{{amount_with_comma_separator}}", formattedMoney)
      .replace("{{amount_no_decimals}}", Math.round(cents / 100).toString())
      .replace(
        "{{amount_with_space_separator}}",
        formattedMoney.replace(/\B(?=(\d{3})+(?!\d))/g, " "),
      );
  }
}

// Register custom elements
if (!customElements.get("inspirations-component")) {
  customElements.define("inspirations-component", InspirationsComponent);
}

if (!customElements.get("inspiration-bundle-drawer")) {
  customElements.define("inspiration-bundle-drawer", InspirationBundleDrawer);
}
